import { initTheme } from "@uzverse/ui";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { BrowserRouter } from "react-router-dom";
import { createHttpApi } from "./api/http.js";
import type { Services } from "./api/types.js";
import { App } from "./App.js";
import { createAppI18n } from "./i18n/index.js";
import { createWebSocketConnection } from "./realtime/ws.js";
import { SessionProvider } from "./state/session.js";
import "@uzverse/ui/styles.css";
import "./styles.css";

initTheme();

async function createServices(): Promise<Services> {
  if (import.meta.env["VITE_UZ_MOCK"] === "1") {
    // Dev/e2e only: the in-browser fake gateway from the testkit. Never part of a real build.
    const { createMockServices } = await import("./mock/backend.js");
    return createMockServices();
  }
  const wsUrl =
    (import.meta.env["VITE_UZ_GATEWAY_WS"] as string | undefined) ??
    `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;
  return { api: createHttpApi(), connect: () => createWebSocketConnection(wsUrl) };
}

const root = document.getElementById("root");
if (!root) throw new Error("missing #root");
const i18n = createAppI18n();
void createServices().then((services) => {
  createRoot(root).render(
    <StrictMode>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter>
          <SessionProvider services={services}>
            <App />
          </SessionProvider>
        </BrowserRouter>
      </I18nextProvider>
    </StrictMode>,
  );
});
